// Builds the fused per-pixel colour shader (§6.2 passes 3–10 in M3; more passes join in M7–M10).
// Each pass is a GLSL block; a pass whose settings are all zero is left out of the program entirely
// (its #define is absent), so it costs nothing. Programs are cached by mode + define set.
//
// Modes:
//  · 'region'      — export/100% path, plan order: colour on a source region → premultiplied target
//                    (the warp pass then resamples it with Catmull-Rom, then the output pass).
//  · 'warpLinear'  — preview: geometry sample + colour in ONE pass → linear straight-alpha target.
//  · 'warpOutput'  — preview to screen: geometry sample + colour + output transform in ONE pass.
//    PREVIEW DEVIATION (recorded): the preview resamples the source before the colour math instead of
//    after (sub-pixel difference); one full-resolution pass instead of three keeps the frame time down
//    on integrated GPUs (measured: a trivial 2560×1707 RGBA16F pass costs ~6.4 ms on Iris Xe).
import { COLOR_FNS, HEADER, OKLAB_FNS } from './common';
import { MASK_FNS } from './localPasses';

export type ColorFeatures = {
  wb: boolean;
  exposure: boolean;
  tone: boolean;
  curve: boolean;
  whitesBlacks: boolean;
  /** §6.5 colour (OkLCh): vibrance / saturation, with HSL bands when `hsl` is on. */
  color: boolean;
  hsl: boolean;
  /** §11.4 local masks (pass A after exposure, pass B after colour). */
  masks: boolean;
  /** §11.11 clean whites. */
  cleanWhites: boolean;
  /** §6.2 pass 14: a 3D or 1D `.cube` LUT (decided by the renderer, which owns the texture). */
  lut3?: boolean;
  lut1?: boolean;
  /** §11.19 split toning (OkLCh, after Color). */
  splitTone: boolean;
};
export type ColorMode = 'region' | 'warpLinear' | 'warpOutput';

export const featureKey = (f: ColorFeatures) =>
  Object.entries(f)
    .filter(([, v]) => v)
    .map(([k]) => k)
    .join('+') || 'none';

export function colorShader(f: ColorFeatures, mode: ColorMode = 'region'): string {
  const defs = Object.entries(f)
    .filter(([, v]) => v)
    .map(([k]) => `#define F_${k.toUpperCase()} 1`)
    .join('\n');
  const modeDef = `#define MODE_${mode.toUpperCase()} 1`;
  return `${HEADER}${defs}
${modeDef}
${COLOR_FNS}
${OKLAB_FNS}
${MASK_FNS}
uniform sampler2D uSrc;      // region: linear (or encoded 8-bit when uEncoded == 1); warp modes: PREMULTIPLIED linear preview
uniform int uEncoded;
uniform vec4 uSrcRect;       // region mode: the part of uSrc this target covers (texture-normalized)
uniform vec2 uOutSize;
uniform vec4 uRegion;        // region mode: this target covers source-normalized rect (x, y, w, h)
uniform mat3 uOutToSrc;      // warp modes: output pixel → source normalized
uniform sampler2D uGuide;    // full-image low-res blurred luminance (R)
uniform mat3 uWb;
uniform float uGain;
uniform vec3 uTone;          // highlights, shadows, contrast (−100..100)
uniform vec2 uWbk;           // whites, blacks
uniform sampler2D uCurve;    // 1024 × 4 R16F: master, r, g, b
uniform vec3 uHsl[8];        // per band: h, s, l (−100..100)
uniform vec2 uColorAdj;      // vibrance, saturation
uniform vec4 uMaskAdj[8];    // per mask: exposure (EV), highlights, shadows, contrast
uniform float uMaskSat[8];   // per mask: saturation
uniform vec3 uMaskAdj2[8];   // per mask (M22): hue shift (degrees), vibrance, clarity (−100..100)
uniform mat3 uMaskWb[8];     // per mask: temp/tint matrix
uniform vec2 uCw;            // clean whites: amount, use brush (1) / whole image (0)
uniform sampler2D uCwMask;
#ifdef F_LUT3
uniform sampler3D uLut3;
#endif
#ifdef F_LUT1
uniform sampler2D uLut1;
#endif
uniform vec3 uLutMin;
uniform vec3 uLutMax;
uniform float uLutSize;
uniform float uLutAmt;      // intensity 0..1
uniform vec4 uSplit;         // shadow hue (deg), shadow sat, highlight hue (deg), highlight sat (0..100)
uniform float uSplitBalance; // -100..100
// warpOutput
uniform mat3 uToOut;
uniform int uAdobe;
uniform int uFlatten;
uniform vec3 uFill;
uniform int uClipping;
uniform vec2 uOrigin;
uniform float uCanvasH;      // > 0: drawing to the default framebuffer (flip y)
out vec4 o;

float smoothstep01(float e0, float e1, float x) { float t = clamp((x - e0) / (e1 - e0), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }

vec3 passTone(vec3 c, vec2 srcUv, vec3 tone) {
  float Y = max(lumaP3(c), 1e-6);
  float L = log2(Y / 0.18);
  float Lb = log2(max(texture(uGuide, srcUv).r, 1e-6) / 0.18);
  float Lm = mix(L, Lb, 0.6);
  float dL = tone.y / 100.0 * 1.5 * (1.0 - smoothstep01(-6.0, 0.0, Lm));
  dL += tone.x / 100.0 * 1.5 * smoothstep01(0.0, 3.0, Lm);
  float L2 = (L + dL) * (1.0 + tone.z / 100.0 * 0.5);
  float Y2 = 0.18 * exp2(L2);
  return c * (Y2 / Y);
}
float shoulder(float x) { return x > 0.85 ? 0.85 + 0.15 * (1.0 - exp(-(x - 0.85) / 0.15)) : x; }
float curveAt(float v, float row) { return texture(uCurve, vec2((clamp(v, 0.0, 1.0) * 1023.0 + 0.5) / 1024.0, (row + 0.5) / 4.0)).r; }

vec3 colorPipeline(vec3 c, vec2 srcUv) {
#ifdef F_WB
  c = uWb * c;
#endif
#ifdef F_EXPOSURE
  c *= uGain;
#endif
#ifdef F_MASKS
  // Luminance at the same stage as the guide (after WB + exposure): mask clarity compares THIS with the
  // blurred guide, so later edits (mask exposure, tone, curve) do not leak into its detail term.
  float Lpre = log2(max(lumaP3(c), 1e-6) / 0.18);
  // Local pass A (§6.2 pass 5): exposure, temp/tint, highlights/shadows, weighted by each mask.
  for (int i = 0; i < 8; i++) {
    if (i >= uMaskN) break;
    float w = maskW(i, srcUv);
    if (w <= 0.0) continue;
    vec4 a = uMaskAdj[i];
    c *= exp2(w * a.x);
    c = mix(c, uMaskWb[i] * c, w);
    if (a.y != 0.0 || a.z != 0.0) c = passTone(c, srcUv, vec3(a.y * w, a.z * w, 0.0));
  }
#endif
#ifdef F_TONE
  c = passTone(c, srcUv, uTone);
#endif
  // Highlight shoulder: always on (§6.2 pass 7).
  c = vec3(shoulder(c.r), shoulder(c.g), shoulder(c.b));
#if defined(F_WHITESBLACKS) || defined(F_CURVE)
  vec3 v = encodeSigned(c);
#ifdef F_WHITESBLACKS
  v = v + uWbk.x / 100.0 * 0.2 * pow(max(v, 0.0), vec3(4.0));
  v = v + uWbk.y / 100.0 * 0.12 * pow(max(1.0 - v, 0.0), vec3(4.0));
#endif
  v = clamp(v, 0.0, 1.0);
#ifdef F_CURVE
  v = vec3(curveAt(v.r, 0.0), curveAt(v.g, 0.0), curveAt(v.b, 0.0));
  v = vec3(curveAt(v.r, 1.0), curveAt(v.g, 2.0), curveAt(v.b, 3.0));
#endif
  c = decodeSigned(v);
#endif
#ifdef F_COLOR
  {
    vec3 lch = toLch(p3ToOklab(c));
    float L = lch.x, C = lch.y, h = lch.z;
    float cw = smoothstep01(0.01, 0.06, C);
#ifdef F_HSL
    float dh = 0.0, ds = 0.0, dl = 0.0;
    for (int i = 0; i < 8; i++) {
      float w = bandWeight(i, h);
      dh += w * uHsl[i].x;
      ds += w * uHsl[i].y;
      dl += w * uHsl[i].z;
    }
    h = mod(h + dh / 100.0 * 30.0 * cw + 360.0, 360.0);
    C *= 1.0 + ds / 100.0 * cw;
    L += dl / 100.0 * 0.12 * cw;
#endif
    C *= 1.0 + uColorAdj.y / 100.0;
    if (uColorAdj.x != 0.0) {
      float skin = (h >= 35.0 && h <= 75.0) ? 0.5 : 1.0;
      C *= 1.0 + uColorAdj.x / 100.0 * skin * (1.0 - C / 0.3);
    }
    c = oklabToP3(fromLch(vec3(L, max(C, 0.0), h)));
  }
#endif
#ifdef F_MASKS
  // Local pass B (§6.2 pass 11): contrast and saturation, weighted by each mask.
  for (int i = 0; i < 8; i++) {
    if (i >= uMaskN) break;
    float w = maskW(i, srcUv);
    if (w <= 0.0) continue;
    float con = uMaskAdj[i].w, sat = uMaskSat[i];
    vec3 a2 = uMaskAdj2[i];
    if (con != 0.0) {
      float Y = max(lumaP3(c), 1e-6);
      float Ll = log2(Y / 0.18);
      c *= 0.18 * exp2(Ll * (1.0 + w * con / 100.0 * 0.5)) / Y;
    }
    if (a2.z != 0.0) {
      // M22 clarity: local contrast against the blurred luminance guide, strongest in the midtones.
      // DEVIATION: the guide is the 1/8-size blurred luminance (a wide radius), not a dedicated blur.
      float Y = max(lumaP3(c), 1e-6);
      float Ll = log2(Y / 0.18);
      float Lb = log2(max(texture(uGuide, srcUv).r, 1e-6) / 0.18);
      float mid = 1.0 - smoothstep01(2.0, 4.0, abs(Ll + 1.0));
      c *= exp2(w * a2.z / 100.0 * 0.6 * (Lpre - Lb) * mid);
    }
    if (sat != 0.0 || a2.x != 0.0 || a2.y != 0.0) {
      vec3 lch = toLch(p3ToOklab(c));
      float C = lch.y * (1.0 + w * sat / 100.0);
      if (a2.y != 0.0) C *= 1.0 + w * a2.y / 100.0 * (1.0 - min(C / 0.3, 1.0));
      // Hue shift only moves coloured pixels (a grey has no hue to move).
      float h = mod(lch.z + w * a2.x * smoothstep01(0.01, 0.06, lch.y) + 360.0, 360.0);
      c = oklabToP3(fromLch(vec3(lch.x, max(C, 0.0), h)));
    }
  }
#endif
#ifdef F_SPLITTONE
  // Split toning (§11.19): chroma added along the shadow / highlight hues, weighted by OkLab L around a
  // pivot moved by balance. Neutrals pick up the tint; colours shift gently.
  {
    vec3 lab = p3ToOklab(c);
    float pivot = 0.5 + uSplitBalance / 100.0 * 0.25;
    float wh = smoothstep01(pivot - 0.25, pivot + 0.25, lab.x);
    float ws = 1.0 - wh;
    float hs = radians(uSplit.x), hh = radians(uSplit.z);
    lab.yz += ws * uSplit.y / 100.0 * 0.06 * vec2(cos(hs), sin(hs));
    lab.yz += wh * uSplit.w / 100.0 * 0.06 * vec2(cos(hh), sin(hh));
    c = oklabToP3(lab);
  }
#endif
#ifdef F_CLEANWHITES
  // Clean whites (§11.11): near-neutral bright pixels lose chroma and lift toward L 0.97; food is untouched.
  {
    vec3 lab = p3ToOklab(c);
    float wgt = smoothstep01(0.6, 0.7, lab.x) * (1.0 - smoothstep01(0.03, 0.05, length(lab.yz))) * uCw.x / 100.0;
    if (uCw.y > 0.5) wgt *= texture(uCwMask, srcUv).r;
    lab.yz *= 1.0 - wgt;
    lab.x += (0.97 - lab.x) * wgt * 0.5;
    c = oklabToP3(lab);
  }
#endif
#if defined(F_LUT3) || defined(F_LUT1)
  // LUT (§6.2 pass 14) in the sRGB-encoded domain, mixed by intensity.
  {
    vec3 e0 = encodeSigned(c);
    vec3 u = clamp((clamp(e0, 0.0, 1.0) - uLutMin) / (uLutMax - uLutMin), 0.0, 1.0);
    vec3 t = u * (uLutSize - 1.0) / uLutSize + 0.5 / uLutSize;
#ifdef F_LUT3
    vec3 r = texture(uLut3, t).rgb;
#else
    vec3 r = vec3(texture(uLut1, vec2(t.r, 0.5)).r, texture(uLut1, vec2(t.g, 0.5)).g, texture(uLut1, vec2(t.b, 0.5)).b);
#endif
    c = decodeSigned(mix(e0, r, uLutAmt));
  }
#endif
  return c;
}

float enc(float l) { return uAdobe == 1 ? pow(max(l, 0.0), 1.0 / 2.19921875) : linToSrgb(max(l, 0.0)); }

void main() {
#ifdef MODE_REGION
  vec2 p = gl_FragCoord.xy / uOutSize;
  vec2 srcUv = uRegion.xy + p * uRegion.zw;
  vec4 s = texture(uSrc, uSrcRect.xy + p * uSrcRect.zw);
  vec3 c = uEncoded == 1 ? srgbToLin3(s.rgb) : s.rgb;
  c = colorPipeline(c, srcUv);
  // Premultiplied for the resampling passes that follow (§6.2 alpha rule).
  o = vec4(c * s.a, s.a);
#else
  vec2 fc = gl_FragCoord.xy - uOrigin;
  if (uCanvasH > 0.0) fc.y = uOutSize.y - fc.y;
  vec3 h = uOutToSrc * vec3(fc, 1.0);
  vec2 srcUv = h.xy / h.z;
  if (srcUv.x < 0.0 || srcUv.y < 0.0 || srcUv.x > 1.0 || srcUv.y > 1.0) {
#ifdef MODE_WARPOUTPUT
    o = uFlatten == 1 ? vec4(uFill, 1.0) : vec4(0.0);
#else
    o = vec4(0.0);
#endif
    return;
  }
  vec4 s = texture(uSrc, srcUv);           // premultiplied → correct bilinear filtering
  float a = clamp(s.a, 0.0, 1.0);
  vec3 c = a > 1e-6 ? s.rgb / a : vec3(0.0);
  c = colorPipeline(c, srcUv);
#ifdef MODE_WARPOUTPUT
  vec3 lin = clamp(gamutOut(c, uToOut), 0.0, 1.0);
  vec3 e = vec3(enc(lin.r), enc(lin.g), enc(lin.b));
  if (uClipping == 1) {
    if (max(e.r, max(e.g, e.b)) >= 0.998) e = vec3(1.0, 0.0, 0.0);
    else if (max(e.r, max(e.g, e.b)) <= 0.002) e = vec3(0.0, 0.25, 1.0);
  }
  if (uFlatten == 1) { e = mix(uFill, e, a); a = 1.0; }
  o = vec4(e, a);
#else
  o = vec4(c, a);
#endif
#endif
}`;
}
